"use client";

import { useCallback, useEffect, useState } from "react";
import { config } from "@/lib/config";
import { BRAND, SURFACE } from "@/lib/constant";
import { mergeBookingSummary, toBookingSummary } from "@/lib/utils/cal";
import { BookingSummary, CalBookingPayload } from "@/lib/types/cal";
const calLink = config.cal.link;
const NAMESPACE = config.cal.namespace;

// Themes the inline booker and swaps Cal's own confirmation screen for ours by
// catching `bookingSuccessful`.
//
// The booker itself is Cal's <Cal /> inline embed rendered inside the page —
// there is no popup here on purpose. This hook only configures that embed's
// namespace and listens to it, so it is mounted once at the app root.
//
// `active` is what keeps Cal off the critical path. The hook lives at the root
// so its listeners are registered once, but the root is on every route while
// the booker is one section of one page. Until something actually asks for the
// booker — it scrolls into view, or a "Book Now" fires — `@calcom/embed-react`
// is never imported, so none of Cal's embed script, fonts or timezone table is
// fetched and none of its main-thread cost is paid.
export const useCalEmbed = (fallbackTitle: string, hostName: string, active: boolean) => {
  const [booking, setBooking] = useState<BookingSummary | null>(null);
  // Flips once `cal("ui")` has been applied. The embed waits on it, so the
  // iframe can never initialise before the theme and render in Cal's purple.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!calLink || !active) return;
    let cancelled = false;
    (async () => {
      // Imported here rather than at module scope: a static import would put
      // the whole embed SDK in the first-load bundle of every route.
      const {
        getCalApi
      } = await import("@calcom/embed-react");
      const cal = await getCalApi({
        namespace: NAMESPACE
      });
      if (cancelled) return;

      // Theme the booker so it renders in Octarnal's palette rather than
      // Cal's default purple.
      //
      // Dark on purpose, and pinned rather than left on the visitor's system
      // preference: the booker sits between two dark sections, and a booker
      // that flips to white for half the audience is not a design.
      cal("ui", {
        theme: "dark",
        layout: "month_view",
        cssVarsPerTheme: {
          // Cal's own dark palette is near-black (#101010). Everything dark on
          // this site is the card-foreground shade instead, so the booker is
          // rebuilt on that surface: SURFACE for the sheet, a step down for the
          // page behind it, two steps up for the day cells and their hovers.
          dark: {
            "cal-brand": BRAND,
            "cal-brand-text": "#000000",
            // black on lime — lime is too pale for white
            "cal-bg": SURFACE,
            "cal-bg-muted": "#1a1a21",
            "cal-bg-subtle": "#2b2b35",
            "cal-bg-emphasis": "#3a3a46",
            "cal-border": "#3a3a46",
            "cal-border-subtle": "#2f2f3a",
            "cal-border-emphasis": "#4a4a57",
            "cal-border-booker": "#000000",
            "cal-border-booker-width": "2px",
            "cal-text-emphasis": "#ffffff",
            radius: "0.125rem" // matches AppButton's rounded-xs
          },
          // Kept as a floor: if a future Cal release ignores the pinned theme,
          // the light booker still comes out branded rather than purple.
          light: {
            "cal-brand": BRAND,
            "cal-brand-text": "#000000",
            "cal-text-emphasis": "#111111",
            "cal-border-booker": "#000000",
            "cal-border-booker-width": "2px",
            radius: "0.125rem"
          }
        }
      });

      // A reschedule does NOT fire `bookingSuccessful` — Cal emits a separate
      // event for it, which is why the first version fell through to Cal's own
      // confirmation screen after rescheduling. Both are handled, and the V2
      // aliases are registered too since Cal has shipped both names.
      const SUCCESS_EVENTS = [{
        action: "bookingSuccessful",
        rescheduled: false
      }, {
        action: "bookingSuccessfulV2",
        rescheduled: false
      }, {
        action: "rescheduleBookingSuccessful",
        rescheduled: true
      }, {
        action: "rescheduleBookingSuccessfulV2",
        rescheduled: true
      }] as const;
      for (const {
        action,
        rescheduled
      } of SUCCESS_EVENTS) {
        cal("on", {
          action,
          callback: (e: unknown) => {
            // Cal dispatches a CustomEvent; the useful part is detail.data.
            // Probed rather than cast — it crosses an iframe from a third party.
            const detail = (e as {
              detail?: {
                data?: CalBookingPayload;
              };
            })?.detail;

            // Cal's payload shape varies by event and by release, and a shape
            // we fail to read shows up as a confirmation card with empty
            // fields rather than an error. Logging the raw object in dev makes
            // that debuggable instead of silent.
            if (process.env.NODE_ENV !== "production") {
              console.debug(`[cal:${action}]`, detail?.data);
            }

            // Merged, not assigned: Cal fires the original event and its V2
            // alias for the same booking, and the two payloads carry different
            // fields. Assigning let the later one overwrite the richer one.
            setBooking(previous => mergeBookingSummary(previous, {
              ...toBookingSummary(detail?.data, fallbackTitle, hostName),
              rescheduled
            }));
          }
        });
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [fallbackTitle, hostName, active]);
  const dismissBooking = useCallback(() => setBooking(null), []);
  return {
    enabled: Boolean(calLink),
    calLink,
    namespace: NAMESPACE,
    booking,
    dismissBooking,
    ready
  };
};